import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";

export const WEEKDAYS = [
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
  { key: "sun", label: "Sunday" },
];

// "HH:MM" → minutes
const toMin = (t) => {
  if (!t || !t.includes(":")) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};
const JS_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export function defaultAvailability() {
  const weekly = {};
  WEEKDAYS.forEach(({ key }) => {
    weekly[key] = { on: key !== "sun", start: "09:00", end: "18:00" };
  });
  return { weekly, overrides: [] };
}

/**
 * Returns the unavailable minute-ranges for [doctorAvailability] on [dateStr]
 * (YYYY-MM-DD), within the [gridStart, gridEnd] hour window. Each range is
 * { from, to } in minutes-from-midnight. Used to shade the calendar column.
 */
/**
 * The effective working hours for [availability] on [dateStr]:
 *   { start, end, break } in minutes-from-midnight, or null if the day is off.
 * Date overrides win over the weekly schedule.
 */
export function dayHours(availability, dateStr) {
  const av = availability || defaultAvailability();
  const ov = (av.overrides || []).find((o) => o.date === dateStr);
  let day;
  if (ov) {
    if (ov.off) return null;
    day = { on: true, start: ov.start || "09:00", end: ov.end || "18:00" };
  } else {
    const dow = JS_DAY_KEYS[new Date(dateStr + "T00:00:00").getDay()];
    day = (av.weekly || {})[dow];
  }
  if (!day || !day.on) return null;
  const start = toMin(day.start);
  const end = toMin(day.end);
  if (start == null || end == null || end <= start) return null;
  let brk = null;
  if (!ov && day.break && day.break.start && day.break.end) {
    const bs = toMin(day.break.start);
    const be = toMin(day.break.end);
    if (bs != null && be != null && be > bs) brk = { from: bs, to: be };
  }
  return { start, end, break: brk };
}

// The clinic-default day used when a branch's hours can't be read.
const DEFAULT_START = 9 * 60;
const DEFAULT_END = 18 * 60;

/**
 * A BRANCH's bookable hours on [dateStr] — for "Any doctor" bookings, which
 * have no personal roster. Separate from dayHours() (doctors) on purpose.
 *   { closed: true, day }                     the weekday is in closed_days
 *   { start, end, break: null }               from open_time / close_time
 *   { start, end, break: null, fallback }     hours unreadable or overnight →
 *                                             09:00–18:00 (fallback: true)
 * No branch at all → the same 09:00–18:00 default, Sunday closed, no flag.
 * closed_days missing → ["sun"], as the backend returns for legacy branches.
 */
export function branchDayHours(branch, dateStr) {
  const day = JS_DAY_KEYS[new Date(dateStr + "T00:00:00").getDay()];
  const closed = Array.isArray(branch?.closed_days) ? branch.closed_days : ["sun"];
  if (closed.includes(day)) return { closed: true, day };
  if (!branch) return { start: DEFAULT_START, end: DEFAULT_END, break: null };
  const start = toMin(branch.open_time);
  const end = toMin(branch.close_time);
  if (start == null || end == null || end <= start) {
    return { start: DEFAULT_START, end: DEFAULT_END, break: null, fallback: true };
  }
  return { start, end, break: null };
}

export function unavailableRanges(availability, dateStr, gridStartHour, gridEndHour) {
  const gridFrom = gridStartHour * 60;
  const gridTo = gridEndHour * 60;
  const full = [{ from: gridFrom, to: gridTo }];

  const day = dayHours(availability, dateStr);
  if (!day) return full; // whole day off

  const start = Math.max(day.start, gridFrom);
  const end = Math.min(day.end, gridTo);

  const blocks = [];
  if (start > gridFrom) blocks.push({ from: gridFrom, to: start }); // before shift
  if (end < gridTo) blocks.push({ from: end, to: gridTo });         // after shift
  if (day.break) blocks.push({ from: day.break.from, to: day.break.to });
  return blocks;
}

/** Dialog to edit a doctor's availability. `userId`/`userName` required. */
export function AvailabilityDialog({ open, onClose, userId, userName, onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && userId) {
      setData(null);
      api.get(`/users/${userId}/availability`)
        .then((r) => setData(normalize(r.data)))
        .catch(() => setData(defaultAvailability()));
    }
  }, [open, userId]);

  const setDay = (key, patch) =>
    setData((d) => ({ ...d, weekly: { ...d.weekly, [key]: { ...d.weekly[key], ...patch } } }));
  const setBreak = (key, patch) =>
    setData((d) => {
      const cur = d.weekly[key].break || { start: "13:00", end: "14:00" };
      return { ...d, weekly: { ...d.weekly, [key]: { ...d.weekly[key], break: { ...cur, ...patch } } } };
    });
  const toggleBreak = (key, enabled) =>
    setData((d) => ({
      ...d,
      weekly: { ...d.weekly, [key]: { ...d.weekly[key], break: enabled ? { start: "13:00", end: "14:00" } : null } },
    }));

  const addOverride = () =>
    setData((d) => ({ ...d, overrides: [...(d.overrides || []), { date: "", off: true, start: "10:00", end: "14:00" }] }));
  const setOverride = (i, patch) =>
    setData((d) => ({ ...d, overrides: d.overrides.map((o, idx) => (idx === i ? { ...o, ...patch } : o)) }));
  const removeOverride = (i) =>
    setData((d) => ({ ...d, overrides: d.overrides.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        weekly: data.weekly,
        overrides: (data.overrides || []).filter((o) => o.date),
      };
      await api.put(`/users/${userId}/availability`, payload);
      toast.success("Availability saved");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-navy">Availability — {userName}</DialogTitle></DialogHeader>
        {!data ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-4 py-2">
            {/* Weekly hours */}
            <div className="space-y-2">
              {WEEKDAYS.map(({ key, label }) => {
                const day = data.weekly[key] || { on: false };
                return (
                  <div key={key} className="rounded-lg border border-navy/10 p-2.5">
                    <div className="flex items-center gap-3">
                      <Switch checked={!!day.on} onCheckedChange={(v) => setDay(key, { on: v })} data-testid={`avail-${key}-on`} />
                      <span className="w-24 text-sm font-medium text-ink">{label}</span>
                      {day.on ? (
                        <div className="flex items-center gap-1.5">
                          <Input type="time" value={day.start || "09:00"} onChange={(e) => setDay(key, { start: e.target.value })} className="h-8 w-28 bg-white" />
                          <span className="text-xs text-muted-foreground">to</span>
                          <Input type="time" value={day.end || "18:00"} onChange={(e) => setDay(key, { end: e.target.value })} className="h-8 w-28 bg-white" />
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Off</span>
                      )}
                    </div>
                    {day.on && (
                      <div className="mt-2 flex items-center gap-3 pl-[3.25rem]">
                        <Switch checked={!!day.break} onCheckedChange={(v) => toggleBreak(key, v)} data-testid={`avail-${key}-break`} />
                        <span className="text-xs text-muted-foreground">Break</span>
                        {day.break && (
                          <div className="flex items-center gap-1.5">
                            <Input type="time" value={day.break.start} onChange={(e) => setBreak(key, { start: e.target.value })} className="h-8 w-28 bg-white" />
                            <span className="text-xs text-muted-foreground">to</span>
                            <Input type="time" value={day.break.end} onChange={(e) => setBreak(key, { end: e.target.value })} className="h-8 w-28 bg-white" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Overrides */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-semibold text-navy">Date overrides (leave / special hours)</p>
                <Button variant="outline" size="sm" onClick={addOverride} data-testid="avail-add-override"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
              </div>
              {(data.overrides || []).length === 0 && <p className="text-xs text-muted-foreground">No overrides.</p>}
              <div className="space-y-2">
                {(data.overrides || []).map((o, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg border border-navy/10 p-2">
                    <Input type="date" value={o.date} onChange={(e) => setOverride(i, { date: e.target.value })} className="h-8 w-36 bg-white" />
                    <div className="flex items-center gap-1.5">
                      <Switch checked={!!o.off} onCheckedChange={(v) => setOverride(i, { off: v })} />
                      <span className="text-xs text-muted-foreground">Day off</span>
                    </div>
                    {!o.off && (
                      <div className="flex items-center gap-1.5">
                        <Input type="time" value={o.start || "10:00"} onChange={(e) => setOverride(i, { start: e.target.value })} className="h-8 w-28 bg-white" />
                        <span className="text-xs text-muted-foreground">to</span>
                        <Input type="time" value={o.end || "14:00"} onChange={(e) => setOverride(i, { end: e.target.value })} className="h-8 w-28 bg-white" />
                      </div>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeOverride(i)}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!data || saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="avail-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Fill in any missing weekday keys so the editor always has 7 rows.
function normalize(av) {
  const base = defaultAvailability();
  const weekly = { ...base.weekly };
  WEEKDAYS.forEach(({ key }) => {
    if (av?.weekly?.[key]) weekly[key] = { ...base.weekly[key], ...av.weekly[key] };
  });
  return { weekly, overrides: av?.overrides || [] };
}
